import React from "react";
import { Grid } from "@mui/material";
import FinancialTable from "./components/FinancialTable";
import {
  FinancialTableConfig,
  FinancialTableDataType,
  FinancialTableRowType,
  FINANCIAL_STATEMENT_ROW_KEYS,
  COMMON_FY_COLUMNS,
} from "./constants/financialStatementTable";

// Example table configurations - these should be replaced with actual data from props/state/API
const getFinancialStatementTables = (): FinancialTableConfig[] => {
  return [
    // Profit & Loss Analysis Table
    {
      title: "Profit & Loss Analysis",
      columns: [
        { key: "fy2023", label: "FY2023" },
        { key: "fy2024", label: "FY2024" },
        { key: "fy2025", label: "FY2025" },
        { key: "forecast2026", label: "Forecast 2026" },
      ],
      rows: [
        {
          key: FINANCIAL_STATEMENT_ROW_KEYS.SALES,
          label: FINANCIAL_STATEMENT_ROW_KEYS.SALES,
          type: FinancialTableRowType.DATA,
          dataType: FinancialTableDataType.CURRENCY,
          values: ["", 248425341, 248425341, 248425341],
        },
        {
          key: FINANCIAL_STATEMENT_ROW_KEYS.COST_OF_GOODS_SOLD,
          label: FINANCIAL_STATEMENT_ROW_KEYS.COST_OF_GOODS_SOLD,
          type: FinancialTableRowType.DATA,
          dataType: FinancialTableDataType.CURRENCY,
          values: ["", -2195247, -2195247, -2195247],
        },
        {
          key: FINANCIAL_STATEMENT_ROW_KEYS.GROSS_PROFIT,
          label: FINANCIAL_STATEMENT_ROW_KEYS.GROSS_PROFIT,
          type: FinancialTableRowType.DATA,
          dataType: FinancialTableDataType.CURRENCY,
          values: ["", 246230094, 246230094, 246230094],
        //   isKeyRow: true,
        },
        {
          key: FINANCIAL_STATEMENT_ROW_KEYS.DEPRECIATION_ADD_BACK,
          label: FINANCIAL_STATEMENT_ROW_KEYS.DEPRECIATION_ADD_BACK,
          type: FinancialTableRowType.DATA,
          dataType: FinancialTableDataType.CURRENCY,
          values: ["", 2613187, 2608754, 2604329],
        },
        {
          key: FINANCIAL_STATEMENT_ROW_KEYS.INTEREST_EXPENSE_ADD_BACK,
          label: FINANCIAL_STATEMENT_ROW_KEYS.INTEREST_EXPENSE_ADD_BACK,
          type: FinancialTableRowType.DATA,
          dataType: FinancialTableDataType.CURRENCY,
          values: ["", 1860705, 1863686, 1866672],
        },
        {
          key: FINANCIAL_STATEMENT_ROW_KEYS.OPERATING_EXPENSES,
          label: FINANCIAL_STATEMENT_ROW_KEYS.OPERATING_EXPENSES,
          type: FinancialTableRowType.DATA,
          dataType: FinancialTableDataType.CURRENCY,
          values: ["", 202535954, 214572038, 227323389],
        },
        {
          key: FINANCIAL_STATEMENT_ROW_KEYS.OPERATING_PROFIT_BEFORE_TAX,
          label: FINANCIAL_STATEMENT_ROW_KEYS.OPERATING_PROFIT_BEFORE_TAX,
          type: FinancialTableRowType.DATA,
          dataType: FinancialTableDataType.CURRENCY,
          values: ["", 246230094, 246230094, 246230094],
        },
        {
          key: FINANCIAL_STATEMENT_ROW_KEYS.NET_PROFIT_AFTER_TAX,
          label: FINANCIAL_STATEMENT_ROW_KEYS.NET_PROFIT_AFTER_TAX,
          type: FinancialTableRowType.DATA,
          dataType: FinancialTableDataType.CURRENCY,
          values: ["", 246230094, 246230094, 246230094],
        },
        {
          key: FINANCIAL_STATEMENT_ROW_KEYS.EBITDA,
          label: FINANCIAL_STATEMENT_ROW_KEYS.EBITDA,
          type: FinancialTableRowType.SUMMARY,
          dataType: FinancialTableDataType.CURRENCY,
          values: ["", 246230094, 246230094, 246230094],
          isSummary: true,
        },
      ],
    },
    // Profit & Loss Growth Rates Table
    {
      title: "Profit & Loss Growth Rates",
      columns: [
        { key: "fy2024", label: "FY2024" },
        { key: "fy2025", label: "FY2025" },
        { key: "forecast2026", label: "Forecast 2026" },
      ],
      rows: [
        {
          key: FINANCIAL_STATEMENT_ROW_KEYS.SALES_GROWTH_RATE,
          label: FINANCIAL_STATEMENT_ROW_KEYS.SALES_GROWTH_RATE,
          type: FinancialTableRowType.DATA,
          dataType: FinancialTableDataType.PERCENTAGE,
          values: ["", 7.3, 7.3],
        },
        {
          key: FINANCIAL_STATEMENT_ROW_KEYS.OPERATING_COSTS_GROWTH_RATE,
          label: FINANCIAL_STATEMENT_ROW_KEYS.OPERATING_COSTS_GROWTH_RATE,
          type: FinancialTableRowType.DATA,
          dataType: FinancialTableDataType.PERCENTAGE,
          values: ["", -0.9, -0.9],
        },
        {
          key: FINANCIAL_STATEMENT_ROW_KEYS.COGS_GROWTH_RATE,
          label: FINANCIAL_STATEMENT_ROW_KEYS.COGS_GROWTH_RATE,
          type: FinancialTableRowType.DATA,
          dataType: FinancialTableDataType.PERCENTAGE,
          values: ["", 7.3, 7.3],
        },
        {
          key: FINANCIAL_STATEMENT_ROW_KEYS.EBITDA_GROWTH_RATE,
          label: FINANCIAL_STATEMENT_ROW_KEYS.EBITDA_GROWTH_RATE,
          type: FinancialTableRowType.DATA,
          dataType: FinancialTableDataType.PERCENTAGE,
          values: ["", 7.3, 7.3],
        },
      ],
    },
    // Profitability Margins Table
    {
      title: "Profitability Margins",
      columns: [
        { key: "fy2023", label: "FY 2023" },
        { key: "fy2024", label: "FY2024" },
        { key: "forecast2025", label: "Forecast 2025" },
      ],
      rows: [
        {
          key: FINANCIAL_STATEMENT_ROW_KEYS.GROSS_PROFIT_MARGIN,
          label: FINANCIAL_STATEMENT_ROW_KEYS.GROSS_PROFIT_MARGIN,
          type: FinancialTableRowType.DATA,
          dataType: FinancialTableDataType.PERCENTAGE,
          values: [81.5, 81.5, 81.5],
        },
        {
          key: FINANCIAL_STATEMENT_ROW_KEYS.EBITDA_PROFIT_MARGIN,
          label: FINANCIAL_STATEMENT_ROW_KEYS.EBITDA_PROFIT_MARGIN,
          type: FinancialTableRowType.DATA,
          dataType: FinancialTableDataType.PERCENTAGE,
          values: [81.5, 81.5, 81.5],
        },
        {
          key: FINANCIAL_STATEMENT_ROW_KEYS.NET_PROFIT_MARGIN,
          label: FINANCIAL_STATEMENT_ROW_KEYS.NET_PROFIT_MARGIN,
          type: FinancialTableRowType.DATA,
          dataType: FinancialTableDataType.PERCENTAGE,
          values: [-0.7, -0.7, -0.7],
        },
      ],
    },
    // DSCR Table
    {
      title: "Debt Servicing Coverage Ratio (DSCR)",
      //   columns: [{ key: "current", label: "" }],
      columns: [],
      rows: [
        {
          key: FINANCIAL_STATEMENT_ROW_KEYS.EXISTING_LOAN_COMMITMENTS,
          label: FINANCIAL_STATEMENT_ROW_KEYS.EXISTING_LOAN_COMMITMENTS,
          type: FinancialTableRowType.DATA,
          dataType: FinancialTableDataType.CURRENCY,
          values: [309464],
        },
        {
          key: FINANCIAL_STATEMENT_ROW_KEYS.NEW_LOAN_COMMITMENT,
          label: FINANCIAL_STATEMENT_ROW_KEYS.NEW_LOAN_COMMITMENT,
          type: FinancialTableRowType.DATA,
          dataType: FinancialTableDataType.CURRENCY,
          values: [250000],
        },
        {
          key: FINANCIAL_STATEMENT_ROW_KEYS.UPDATED_DSCR_RATIO,
          label: FINANCIAL_STATEMENT_ROW_KEYS.UPDATED_DSCR_RATIO,
          type: FinancialTableRowType.SUMMARY,
          dataType: FinancialTableDataType.NUMBER,
          values: [101.12],
          isSummary: true,
        },
      ],
    },
    // ICR Table
    {
      title: "Interest Coverage Ratio (ICR)",
      //   columns: [{ key: "current", label: "" }],
      columns: [],
      rows: [
        {
          key: FINANCIAL_STATEMENT_ROW_KEYS.EXISTING_INTEREST_REPAYMENTS,
          label: FINANCIAL_STATEMENT_ROW_KEYS.EXISTING_INTEREST_REPAYMENTS,
          type: FinancialTableRowType.DATA,
          dataType: FinancialTableDataType.CURRENCY,
          values: [1863686],
        },
        {
          key: FINANCIAL_STATEMENT_ROW_KEYS.NEW_INTEREST_REPAYMENTS,
          label: FINANCIAL_STATEMENT_ROW_KEYS.NEW_INTEREST_REPAYMENTS,
          type: FinancialTableRowType.DATA,
          dataType: FinancialTableDataType.CURRENCY,
          values: [23656],
        },
        {
          key: FINANCIAL_STATEMENT_ROW_KEYS.UPDATED_ICR,
          label: FINANCIAL_STATEMENT_ROW_KEYS.UPDATED_ICR,
          type: FinancialTableRowType.SUMMARY,
          dataType: FinancialTableDataType.NUMBER,
          values: [2.748067918],
          isSummary: true,
        },
      ],
    },
    // Profitability
    {
      title: "New Profitability Servicing",
      // columns: [{ key: "current", label: "" }],
      // columns: [{ key: "current", label: "Testing" }],
      columns: [],
      rows: [
        {
          key: FINANCIAL_STATEMENT_ROW_KEYS.UPDATED_EBITDA,
          label: FINANCIAL_STATEMENT_ROW_KEYS.UPDATED_EBITDA,
          type: FinancialTableRowType.SUMMARY,
          dataType: FinancialTableDataType.CURRENCY,
          values: [56322816],
          isSummary: true,
        },
      ],
    },
    // Balance Sheet Analysis
    {
      title: "Balance Sheet Analysis",
      columns: COMMON_FY_COLUMNS,
      rows: [
        {
          key: FINANCIAL_STATEMENT_ROW_KEYS.CURRENT_ASSETS,
          label: FINANCIAL_STATEMENT_ROW_KEYS.CURRENT_ASSETS,
          type: FinancialTableRowType.DATA,
          dataType: FinancialTableDataType.CURRENCY,
          values: ["", 53446448, 52380255, 51335331],
        },
        {
          key: FINANCIAL_STATEMENT_ROW_KEYS.CURRENT_LIABILITIES,
          label: FINANCIAL_STATEMENT_ROW_KEYS.CURRENT_LIABILITIES,
          type: FinancialTableRowType.DATA,
          dataType: FinancialTableDataType.CURRENCY,
          values: ["", 62177401, 54821490, 48335822],
        },
        {
          key: FINANCIAL_STATEMENT_ROW_KEYS.TOTAL_CASH_AND_EQUIVALENTS,
          label: FINANCIAL_STATEMENT_ROW_KEYS.TOTAL_CASH_AND_EQUIVALENTS,
          type: FinancialTableRowType.DATA,
          dataType: FinancialTableDataType.CURRENCY,
          values: ["", 2707681, 2459207, 2233535],
        },
        {
          key: FINANCIAL_STATEMENT_ROW_KEYS.ACCOUNTS_RECEIVABLE,
          label: FINANCIAL_STATEMENT_ROW_KEYS.ACCOUNTS_RECEIVABLE,
          type: FinancialTableRowType.DATA,
          dataType: FinancialTableDataType.CURRENCY,
          values: ["", 44353532, 44341781, 44330033],
        },
        {
          key: FINANCIAL_STATEMENT_ROW_KEYS.ACCOUNTS_PAYABLE,
          label: FINANCIAL_STATEMENT_ROW_KEYS.ACCOUNTS_PAYABLE,
          type: FinancialTableRowType.DATA,
          dataType: FinancialTableDataType.CURRENCY,
          values: ["", 38207785, 35286790, 32589106],
        },
        {
          key: FINANCIAL_STATEMENT_ROW_KEYS.INVENTORY,
          label: FINANCIAL_STATEMENT_ROW_KEYS.INVENTORY,
          type: FinancialTableRowType.DATA,
          dataType: FinancialTableDataType.CURRENCY,
          values: ["", 559460, 610310, 665782],
        },
        {
          key: FINANCIAL_STATEMENT_ROW_KEYS.TOTAL_ASSETS,
          label: FINANCIAL_STATEMENT_ROW_KEYS.TOTAL_ASSETS,
          type: FinancialTableRowType.SUMMARY,
          dataType: FinancialTableDataType.CURRENCY,
          values: ["", 75585108, 74229575, 72898352],
          isKeyRow: true,
        },
        {
          key: FINANCIAL_STATEMENT_ROW_KEYS.TOTAL_LIABILITIES,
          label: FINANCIAL_STATEMENT_ROW_KEYS.TOTAL_LIABILITIES,
          type: FinancialTableRowType.SUMMARY,
          dataType: FinancialTableDataType.CURRENCY,
          values: ["", 71638816, 66656232, 62020194],
          isKeyRow: true,
        },
        {
          key: FINANCIAL_STATEMENT_ROW_KEYS.NET_EQUITY,
          label: FINANCIAL_STATEMENT_ROW_KEYS.NET_EQUITY,
          type: FinancialTableRowType.SUMMARY,
          dataType: FinancialTableDataType.CURRENCY,
          values: ["", 3946292, 7573343, 2999510],
          isKeyRow: true,
        },
      ],
    },
    // Leverage Ratios
    {
      title: "Leverage Ratios",
      columns: COMMON_FY_COLUMNS,
      rows: [
        {
          key: FINANCIAL_STATEMENT_ROW_KEYS.DEBT_EQUITY_RATIO,
          label: FINANCIAL_STATEMENT_ROW_KEYS.DEBT_EQUITY_RATIO,
          type: FinancialTableRowType.DATA,
          dataType: FinancialTableDataType.NUMBER,
          values: ["", 18.2, 8.8, 20.7],
        },
        {
          key: FINANCIAL_STATEMENT_ROW_KEYS.DEBT_ASSET_RATIO,
          label: FINANCIAL_STATEMENT_ROW_KEYS.DEBT_ASSET_RATIO,
          type: FinancialTableRowType.DATA,
          dataType: FinancialTableDataType.NUMBER,
          values: ["", 0.9, 0.9, 0.9],
        },
        {
          key: FINANCIAL_STATEMENT_ROW_KEYS.DEBT_EBITDA_RATIO,
          label: FINANCIAL_STATEMENT_ROW_KEYS.DEBT_EBITDA_RATIO,
          type: FinancialTableRowType.DATA,
          dataType: FinancialTableDataType.NUMBER,
          values: ["", 1.3, 1.2, 1.0],
        },
      ],
    },
    // Liquidity Ratio
    {
      title: "Liquidity Ratio",
      columns: COMMON_FY_COLUMNS,
      rows: [
        {
          key: FINANCIAL_STATEMENT_ROW_KEYS.CURRENT_RATIO,
          label: FINANCIAL_STATEMENT_ROW_KEYS.CURRENT_RATIO,
          type: FinancialTableRowType.DATA,
          dataType: FinancialTableDataType.NUMBER,
          values: ["", 0.9, 1.0, 1.1],
        },
        {
          key: FINANCIAL_STATEMENT_ROW_KEYS.CASH_RATIO,
          label: FINANCIAL_STATEMENT_ROW_KEYS.CASH_RATIO,
          type: FinancialTableRowType.DATA,
          dataType: FinancialTableDataType.PERCENTAGE,
          values: ["", 4.0, 4.0, 5.0],
        },
        {
          key: FINANCIAL_STATEMENT_ROW_KEYS.ACC_REC_AS_PERCENT_OF_SALES,
          label: FINANCIAL_STATEMENT_ROW_KEYS.ACC_REC_AS_PERCENT_OF_SALES,
          type: FinancialTableRowType.DATA,
          dataType: FinancialTableDataType.PERCENTAGE,
          values: ["", 18.0, 17.0, 15.0],
        },
      ],
    },
    // Efficiency Ratio
    {
      title: "Efficiency Ratio",
      columns: COMMON_FY_COLUMNS,
      rows: [
        {
          key: FINANCIAL_STATEMENT_ROW_KEYS.INVENTORY_TURNOVER,
          label: FINANCIAL_STATEMENT_ROW_KEYS.INVENTORY_TURNOVER,
          type: FinancialTableRowType.DATA,
          dataType: FinancialTableDataType.NUMBER,
          values: ["", 111.1, 89.8, 72.6],
        },
        {
          key: FINANCIAL_STATEMENT_ROW_KEYS.RECEIVABLES_TURNOVER,
          label: FINANCIAL_STATEMENT_ROW_KEYS.RECEIVABLES_TURNOVER,
          type: FinancialTableRowType.DATA,
          dataType: FinancialTableDataType.NUMBER,
          values: ["", 1.2, 1.2, 1.2],
        },
        {
          key: FINANCIAL_STATEMENT_ROW_KEYS.ASSET_EFFICIENCY_RATIO,
          label: FINANCIAL_STATEMENT_ROW_KEYS.ASSET_EFFICIENCY_RATIO,
          type: FinancialTableRowType.DATA,
          dataType: FinancialTableDataType.NUMBER,
          values: ["", 3.3, 3.6, 3.9],
        },
        {
          key: FINANCIAL_STATEMENT_ROW_KEYS.PAYABLES_TURNOVER,
          label: FINANCIAL_STATEMENT_ROW_KEYS.PAYABLES_TURNOVER,
          type: FinancialTableRowType.DATA,
          dataType: FinancialTableDataType.NUMBER,
          values: ["", 1.2, 1.3, 1.4],
        },
      ],
    },
    // Cash Conversion Cycle
    {
      title: "Cash Conversion Cycle",
      columns: COMMON_FY_COLUMNS,
      rows: [
        {
          key: FINANCIAL_STATEMENT_ROW_KEYS.DAYS_SALES_OF_INVENTORY,
          label: FINANCIAL_STATEMENT_ROW_KEYS.DAYS_SALES_OF_INVENTORY,
          type: FinancialTableRowType.DATA,
          dataType: FinancialTableDataType.NUMBER,
          values: ["", 4.4, 4.9, 5.4],
        },
        {
          key: FINANCIAL_STATEMENT_ROW_KEYS.DAYS_SALES_OUTSTANDING,
          label: FINANCIAL_STATEMENT_ROW_KEYS.DAYS_SALES_OUTSTANDING,
          type: FinancialTableRowType.DATA,
          dataType: FinancialTableDataType.NUMBER,
          values: ["", 65.2, 60.7, 56.5],
        },
        {
          key: FINANCIAL_STATEMENT_ROW_KEYS.DAYS_PAYABLE_OUTSTANDING,
          label: FINANCIAL_STATEMENT_ROW_KEYS.DAYS_PAYABLE_OUTSTANDING,
          type: FinancialTableRowType.DATA,
          dataType: FinancialTableDataType.NUMBER,
          values: ["", 303.3, 282.7, 263.5],
        },
        {
          key: FINANCIAL_STATEMENT_ROW_KEYS.PAYABLES_TURNOVER_CASH_CYCLE,
          label: FINANCIAL_STATEMENT_ROW_KEYS.PAYABLES_TURNOVER_CASH_CYCLE,
          type: FinancialTableRowType.DATA,
          dataType: FinancialTableDataType.NUMBER,
          values: ["", 0.0, 0.0, 0.1],
        },
      ],
    },
    // Cashflow Analysis
    {
      title: "Cashflow Analysis",
      columns: COMMON_FY_COLUMNS,
      rows: [
        {
          key: FINANCIAL_STATEMENT_ROW_KEYS.CASH_FLOW_FROM_OPERATING_ACTIVITIES,
          label: FINANCIAL_STATEMENT_ROW_KEYS.CASH_FLOW_FROM_OPERATING_ACTIVITIES,
          type: FinancialTableRowType.DATA,
          dataType: FinancialTableDataType.CURRENCY,
          values: ["", 963442, 4881389, 24732115],
        },
        {
          key: FINANCIAL_STATEMENT_ROW_KEYS.CASH_FLOW_FROM_INVESTMENT,
          label: FINANCIAL_STATEMENT_ROW_KEYS.CASH_FLOW_FROM_INVESTMENT,
          type: FinancialTableRowType.DATA,
          dataType: FinancialTableDataType.CURRENCY,
          values: ["", -2575227, -2555296, -2535519],
        },
        {
          key: FINANCIAL_STATEMENT_ROW_KEYS.CASH_FLOW_FROM_FINANCING,
          label: FINANCIAL_STATEMENT_ROW_KEYS.CASH_FLOW_FROM_FINANCING,
          type: FinancialTableRowType.DATA,
          dataType: FinancialTableDataType.CURRENCY,
          values: ["", 807592, -2574567, 8207604],
        },
        {
          key: FINANCIAL_STATEMENT_ROW_KEYS.NET_CHANGE_IN_CASHFLOW,
          label: FINANCIAL_STATEMENT_ROW_KEYS.NET_CHANGE_IN_CASHFLOW,
          type: FinancialTableRowType.SUMMARY,
          dataType: FinancialTableDataType.CURRENCY,
          values: ["", -804193, -248474, 30404200],
          isSummary: true,
          useConditionalColoring: true,
        },
      ],
    },
  ];
};

const EntityFinancialStatement = React.memo(() => {
  const tables = getFinancialStatementTables();

  return (
    <Grid
      item
      xs={12}
      style={{
        margin: "20px 0 20px 0",
        padding: "30px 0 10px 0",
      }}
    >
      {tables.map((tableConfig, index) => (
        <FinancialTable key={`financial-table-${index}`} config={tableConfig} />
      ))}
    </Grid>
  );
});

export default EntityFinancialStatement;
